import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Copy, ExternalLink, Clock, Shield, Loader2, Sun, Moon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

export default function UsdaCheckoutPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState('loading');
  const [txHash, setTxHash] = useState(null);
  const [redirectUrl, setRedirectUrl] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [pulse, setPulse] = useState(false);
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);

  // Fetch initial order
  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await axios.get(`${API}/api/payments/usda/order/${orderId}`);
        setOrder(res.data);
        if (res.data.status === 'completed') {
          setStatus('completed');
          setTxHash(res.data.tx_hash);
        } else if (res.data.status === 'expired') {
          setStatus('expired');
        } else {
          setStatus('pending');
          const exp = new Date(res.data.expires_at).getTime();
          setRemaining(Math.max(0, Math.floor((exp - Date.now()) / 1000)));
        }
      } catch {
        setStatus('error');
      }
    };
    fetchOrder();
  }, [orderId]);

  // Poll blockchain every 5 seconds
  useEffect(() => {
    if (status !== 'pending') return;
    intervalRef.current = setInterval(async () => {
      try {
        setPulse(true);
        setTimeout(() => setPulse(false), 600);
        const res = await axios.get(`${API}/api/payments/usda/check/${orderId}`);
        if (res.data.status === 'completed') {
          setStatus('completed');
          setTxHash(res.data.tx_hash);
          setRedirectUrl(res.data.redirect_url);
          clearInterval(intervalRef.current);
          clearInterval(countdownRef.current);
        } else if (res.data.status === 'expired') {
          setStatus('expired');
          clearInterval(intervalRef.current);
          clearInterval(countdownRef.current);
        }
      } catch { /* ignore */ }
    }, 5000);
    return () => clearInterval(intervalRef.current);
  }, [status, orderId]);

  // Countdown timer
  useEffect(() => {
    if (status !== 'pending' || remaining <= 0) return;
    countdownRef.current = setInterval(() => {
      setRemaining(prev => {
        if (prev <= 1) {
          setStatus('expired');
          clearInterval(countdownRef.current);
          clearInterval(intervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, [status, remaining]);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiato!`);
  };

  const formatTime = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  const fmt = (v) => new Intl.NumberFormat('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 6 }).format(v || 0);

  if (status === 'loading') {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#050816]' : 'bg-gradient-to-br from-emerald-50 via-white to-teal-50'}`}>
        <Loader2 className="w-10 h-10 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#050816]' : 'bg-slate-50'}`}>
        <Card className="max-w-md w-full mx-4"><CardContent className="p-8 text-center">
          <XCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <p className="text-lg font-semibold">Ordine non trovato</p>
        </CardContent></Card>
      </div>
    );
  }

  const timerColor = remaining > 120 ? 'text-emerald-400' : remaining > 30 ? 'text-amber-400' : 'text-red-400';
  const timerBar = Math.min((remaining / (15 * 60)) * 100, 100);

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${
      isDark ? 'bg-[#050816]' : 'bg-gradient-to-br from-emerald-50 via-white to-teal-50'
    }`}>
      {/* Animated background particles */}
      {isDark && (
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div key={i} className="absolute rounded-full bg-emerald-500/10 animate-pulse"
              style={{
                width: `${4 + Math.random() * 8}px`, height: `${4 + Math.random() * 8}px`,
                left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`, animationDuration: `${2 + Math.random() * 4}s`
              }}
            />
          ))}
        </div>
      )}

      <button onClick={toggleTheme} data-testid="theme-toggle"
        className={`fixed top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full z-50 transition-all hover:scale-110 ${
          isDark ? 'bg-emerald-500/20 text-yellow-400' : 'bg-white text-slate-600 shadow-md'
        }`}
      >
        {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>

      <Card data-testid="usda-checkout-card" className={`w-full max-w-md shadow-2xl border-0 overflow-hidden ${
        isDark ? 'bg-[#0a0f25]/90 backdrop-blur-xl' : 'bg-white/80 backdrop-blur-xl'
      }`}>
        {/* Header gradient bar */}
        <div className="h-1.5 bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400" />

        <CardContent className="p-6 space-y-5">
          {/* COMPLETED STATE */}
          {status === 'completed' && (
            <div className="text-center space-y-4 py-4" data-testid="usda-payment-success">
              <div className="relative inline-block">
                <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/30 animate-bounce">
                  <CheckCircle className="w-14 h-14 text-white" />
                </div>
              </div>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Pagamento Confermato!
              </h1>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Transazione verificata sulla blockchain Polygon
              </p>
              <div className={`rounded-xl p-4 space-y-2 ${isDark ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-emerald-50'}`}>
                <div className="flex justify-between text-sm">
                  <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Importo</span>
                  <span className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{fmt(order?.usda_amount)} USDA</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Equivalente</span>
                  <span className={isDark ? 'text-slate-300' : 'text-slate-700'}>{fmt(order?.amount_fiat)} {order?.currency}</span>
                </div>
              </div>
              {txHash && (
                <a href={`https://polygonscan.com/tx/${txHash}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-emerald-500 hover:text-emerald-400 transition-colors"
                  data-testid="polygonscan-link"
                >
                  <ExternalLink className="w-3 h-3" /> Vedi su PolygonScan
                </a>
              )}
              <Button onClick={() => {
                if (redirectUrl) {
                  window.location.href = redirectUrl;
                } else {
                  navigate('/');
                }
              }} className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white border-0"
                data-testid="usda-return-btn"
              >
                {redirectUrl ? 'Torna al Negozio' : 'Torna alla Home'}
              </Button>
            </div>
          )}

          {/* EXPIRED STATE */}
          {status === 'expired' && (
            <div className="text-center space-y-4 py-4" data-testid="usda-payment-expired">
              <div className="w-20 h-20 mx-auto rounded-full bg-red-500/20 flex items-center justify-center">
                <Clock className="w-10 h-10 text-red-400" />
              </div>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Pagamento Scaduto</h1>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Il tempo per completare il pagamento è scaduto</p>
              <Button variant="outline" onClick={() => navigate(-1)} className={isDark ? 'border-slate-700 text-white' : ''}>
                Torna indietro
              </Button>
            </div>
          )}

          {/* PENDING STATE — The main show */}
          {status === 'pending' && order && (
            <div className="space-y-5" data-testid="usda-payment-pending">
              {/* Brand */}
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 mb-3">
                  <Shield className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-[11px] font-medium text-emerald-400 tracking-wide">PAGAMENTO SICURO ON-CHAIN</span>
                </div>
                <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>USDA Pay</h2>
              </div>

              {/* Amount */}
              <div className={`rounded-xl p-4 text-center ${isDark ? 'bg-gradient-to-br from-emerald-900/30 to-teal-900/30 border border-emerald-500/20' : 'bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200'}`}>
                <p className={`text-xs mb-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Invia esattamente</p>
                <p data-testid="usda-amount" className={`text-3xl font-black tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  <span className="inline-flex items-center gap-2 cursor-pointer" onClick={() => copyToClipboard(String(order.usda_amount), 'Importo USDA')}>
                    {fmt(order.usda_amount)} <span className="text-emerald-500">USDA</span>
                    <Copy className="w-4 h-4 text-emerald-400 opacity-60 hover:opacity-100 transition-opacity" />
                  </span>
                </p>
                <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  = {fmt(order.amount_fiat)} {order.currency} (1 USDA = 1 USD)
                </p>
              </div>

              {/* QR Code */}
              <div className="flex justify-center">
                <div className={`p-3 rounded-2xl ${isDark ? 'bg-white' : 'bg-white shadow-lg'}`}>
                  <img src={order.qr_code || ''} alt="QR Payment" data-testid="usda-qr-code"
                    className="w-48 h-48 sm:w-56 sm:h-56"
                  />
                </div>
              </div>
              <p className={`text-center text-[11px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                Scansiona con il tuo wallet (MetaMask, Trust Wallet, ecc.)
              </p>

              {/* Wallet address */}
              <div>
                <p className={`text-[10px] uppercase tracking-wider mb-1.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  Indirizzo di ricezione (Polygon)
                </p>
                <div className={`flex items-center gap-2 p-2.5 rounded-lg font-mono text-xs break-all ${
                  isDark ? 'bg-slate-800/50 text-slate-300 border border-slate-700' : 'bg-slate-100 text-slate-700'
                }`}>
                  <span className="flex-1" data-testid="usda-wallet-address">{order.wallet_address}</span>
                  <button onClick={() => copyToClipboard(order.wallet_address, 'Indirizzo')}
                    className="shrink-0 p-1.5 rounded-md hover:bg-emerald-500/20 transition-colors"
                    data-testid="copy-address-btn"
                  >
                    <Copy className="w-4 h-4 text-emerald-400" />
                  </button>
                </div>
              </div>

              {/* Contract info */}
              <div className={`flex items-center justify-between text-[10px] px-1 ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
                <span>Contratto USDA: {order.contract_address?.slice(0, 8)}...{order.contract_address?.slice(-6)}</span>
                <span>Rete: Polygon (ID: 137)</span>
              </div>

              {/* Timer */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <div className={`w-2 h-2 rounded-full ${pulse ? 'animate-ping' : 'animate-pulse'} bg-emerald-400`} />
                    <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      In attesa del pagamento...
                    </span>
                  </div>
                  <span data-testid="usda-timer" className={`text-sm font-mono font-bold ${timerColor}`}>
                    {formatTime(remaining)}
                  </span>
                </div>
                <div className={`h-1 rounded-full overflow-hidden ${isDark ? 'bg-slate-800' : 'bg-slate-200'}`}>
                  <div className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-teal-400 transition-all duration-1000"
                    style={{ width: `${timerBar}%` }}
                  />
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
